import { useState } from 'react';
import { Link } from 'react-router';

import useAccountQuery from 'api/queries/useAccountQuery';
import useCreditsQuery from 'api/queries/useCreditsQuery';
import useSubscriptionQuery from 'api/queries/useSubscriptionQuery';
import { MAX_CREDITS_PER_ORDER } from 'constants/shop';
import EBTCard from 'features/account/EBTCard';
import Payment from 'features/account/Payment';
import PersonalInfo from 'features/account/PersonalInfo';
import ShippingAddress from 'features/account/ShippingAddress';
import { Alert } from 'honeydew/Alert';
import { CoinIcon } from 'honeydew/icons/Credits';
import CustomizeAlertCopy from 'shared/CustomizeAlertCopy';
import { loadStripe } from 'utils/stripeUtils';

import profileStyles from './styles';

const stripePromise = loadStripe();

function Profile() {
    const { data: account = {} } = useAccountQuery();
    const { data: subscription = {}, isLoading } = useSubscriptionQuery();
    const { data: credits = {} } = useCreditsQuery();
    const [pageAlert, setPageAlert] = useState('');
    const { status } = subscription;
    const canceled = status === 'CANCELLED';

    // Static page alerts
    const pageAlerts = {
        'success.shipping': {
            variant: 'success',
            message: 'Your shipping address has updated.',
        },
        'success.shipping.customize': {
            variant: 'success',
            message: (
                <>
                    Your shipping address has updated. <CustomizeAlertCopy />
                </>
            ),
        },
        'success.payment': {
            variant: 'success',
            message: 'Your payment information was successfully updated.',
        },
        'error.payment': {
            variant: 'error',
            message: 'Unable to update billing information.',
        },
    };

    const alert = pageAlert?.message ? pageAlert : (pageAlerts[pageAlert] ?? null);

    if (isLoading || !subscription) {
        return 'Loading your profile...';
    }

    const { sumCreditBalance } = credits;

    return (
        <div className="space-y-20">
            {canceled && (
                <div className="mb-20">
                    <Alert variant="error">Your plan is currently canceled.</Alert>
                </div>
            )}
            <h1 className="text-heading-sm mb-20">My Profile</h1>
            {alert && (
                <Alert variant={alert.variant} onClose={() => setPageAlert('')}>
                    {alert.message}
                </Alert>
            )}
            <section className={profileStyles.section}>
                <h2 className={profileStyles.sectionHeading}>Personal Information</h2>
                <PersonalInfo />
            </section>
            <section className={profileStyles.section}>
                <h2 className={`${profileStyles.sectionHeading} hidden lg:block`}>Shipping Address</h2>
                <ShippingAddress setPageAlert={setPageAlert} />
            </section>
            <section className={profileStyles.section}>
                <h2 className={`${profileStyles.sectionHeading} hidden lg:block`}>Payment Method</h2>
                <Payment setPageAlert={setPageAlert} stripePromise={stripePromise} />
            </section>
            {account?.featureFlags?.featureSnapEnabled && (
                <section className={profileStyles.section}>
                    <EBTCard setPageAlert={setPageAlert} ebtCard={subscription?.ebtCard} />
                </section>
            )}
            <section className={profileStyles.section}>
                <h2 className={profileStyles.sectionHeading}>Promos & Credits</h2>
                <div>
                    <div className="text-body-md flex items-center gap-10">
                        <CoinIcon width={26} />
                        <p>
                            <span className="font-grotesk-bold">{`$${
                                sumCreditBalance > 0 ? sumCreditBalance.toFixed(2) : '0.00'
                            }`}</span>{' '}
                            credits available. A max of ${MAX_CREDITS_PER_ORDER} in credits may be applied to any single
                            order.
                            <Link to="/account/promotions" className="text-body-sm link ml-10">
                                View all
                            </Link>
                        </p>
                    </div>
                    <p className="text-body-sm mt-25">
                        <Link to="/account/promotions" className="link">
                            Have a discount code?
                        </Link>
                    </p>
                </div>
            </section>
            <section className={profileStyles.section}>
                <h2 className={profileStyles.sectionHeading}>Email & Text Notifications</h2>
                <div>
                    <p className="text-body-md mb-10 lg:mb-20">
                        Looking to make changes to your email and text notifications?
                    </p>
                    <Link to="/account/notifications" className="link text-body-sm">
                        Update my notification settings
                    </Link>
                </div>
            </section>
        </div>
    );
}

export { Profile as Component };
